<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, h, onMounted, ref, shallowRef } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

import { Select, CloseBold } from '@element-plus/icons-vue';

// Services
import VehicleRequestService from '@/api/services/vehicleRequest.service';
import VehicleService from '@/api/services/vehicle.service';
import EmployeeService from "@/api/services/employee.service";
import vehicleRequestService from "@/api/services/vehicleRequest.service";
import { filterByNormalizedText } from "@/utils/textSearch";

// Icons Components
import HeaderTitle from "@/components/layout/HeaderTitle";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import IconVehicle from '@/components/icons/IconVehicle.vue';
import IconRestart from '@/components/icons/IconRestart.vue';

// Dialog Components
import VehicleNewRequest from "@/components/shared/Vehicles/VehicleNewRequest.vue";
import VehicleRequestDialog from "@/components/shared/Vehicles/VehicleRequestDialog.vue";
import VehicleCancelDialog from "@/components/shared/Vehicles/VehicleCancelDialog.vue";
import VehicleRejectDialog from "@/components/shared/Vehicles/VehicleRejectDialog.vue";
import VehicleReturn from "@/components/shared/Vehicles/VehicleReturn.vue";
import VehicleConfirmDialog from "@/components/shared/Vehicles/VehicleConfirmDialog.vue";


const vehicleId = ref();
const vehicleUser = ref();
const selectedFromDate = ref(null);
const selectedToDate = ref(null);

const params = ref({})
const page = ref(1)
const itemsPerPage = ref(10);
const totalResults = ref(0);

const vehicles = ref([])
const vehicleRequests = ref([])
const requestStatus = ref();
const employees = ref([]);
const employeesOriginal = ref([]); // datos originales

const dialogRequest = ref(false)
const selectedRequest = ref({})
const closingRequestForAction = ref(false)

const dialogNewRequest = ref(false);
const dialogRejectRequest = ref(false);
const dialogCancelRequest = ref(false);
const dialogReturnRequest = ref(false);
const dialogConfirmRequest = ref(false);

const vehicleReturnRef = ref(null)


const status = ref('')
const filter = ref([{
    name: "Todos",
    active: true,
}, {
    name: "Solicitados",
    active: false,
}, {
    name: "Entregados",
    active: false,
}])

const allStatus = ref([
    { name: 'Pendiente', value: 'pending' },
    { name: 'Aprobado', value: 'approved' },
    { name: 'Confirmado', value: 'confirmed' },
    { name: 'Cerrado', value: 'closed' },
    { name: 'Rechazado', value: 'rejected' },
    { name: 'Cancelado', value: 'canceled' },
    { name: 'Devuelto', value: 'returned' },
])

const VEHICLE_REQUEST_APPROVER_EMAILS = [
    'nbarrado@undanet.com',
    'jcamacho@undanet.com',
]


const authStore = useAuthStore();
const uiStore = useUiStore();

const user = computed(() => {
    return authStore.getUserLogged;
})

const canApproveVehicleRequests = computed(() => {
    const email = user.value?.email?.trim().toLowerCase();
    return user.value?.roles?.includes('ROLE_ADMIN') && VEHICLE_REQUEST_APPROVER_EMAILS.includes(email);
})

const hasButtons = computed(() => {
    return vehicleRequests.value.some(v => canReturnVehicle(v) || canModifyRequest(v) || canCancelVehicle(v) || canConfirmRequest(v) || canRejectRequest(v));
})

const hasKms = computed(() => {
    return vehicleRequests.value.some(v => !!v.traveledKm);
})

const customPrefix = shallowRef({
    render() {
        return h('img', {
            src: '/calendar-blue.svg',
            style: 'width: 16px; height: 16px; margin-right: 10px;',
        });
    },
})

onMounted(async () => {
    params.value = {};
    await Promise.all([getVehicles(), getEmployees(), getVehicleRequests()]);
})

// search bar

const search = async () => {
    params.value = {};
    params.value['vehicle.id'] = vehicleId.value;
    params.value['employee.id'] = vehicleUser.value;
    params.value['startDate[after]'] = selectedFromDate.value;
    params.value['endDate[before]'] = selectedToDate.value;
    params.value['status'] = requestStatus.value;
    params.value['order[startDate]'] = "ASC";
    if (status.value) {
        params.value.status = status.value;
    }
    await getVehicleRequests();
}

// eslint-disable-next-line no-unused-vars
const resetVehicles = () => {
    page.value = 1;
    itemsPerPage.value = 10;
    params.value = {};
    if (status.value) {
        params.value.status = status.value;
    }
    getVehicleRequests();
}

// Filters
const changeFilter = (index) => {
    let temp = "";
    filter.value.forEach((item, i) => {
        item.active = i === index
        if (item.active) {
            temp = item.name
        }
    })
    switch (temp) {
        case 'Todos':
            status.value = null;
            break;
        case 'Solicitados':
            status.value = ['pending', 'approved']
            break;
        case 'Entregados':
            status.value = ['closed', 'returned', 'confirmed']
            break;
        default:
            break;
    }
    params.value.status = status.value;
    page.value = 1;
    getVehicleRequests();
}

// Getters
const getVehicleRequests = async () => {
    params.value.page = page.value;
    params.value.itemsPerPage = itemsPerPage.value;
    params.value['order[startDate]'] = "ASC";
    uiStore.startLoading();
    try {
        const res = await VehicleRequestService.getVehicleRequests(params.value);
        vehicleRequests.value = res.data['hydra:member']
        totalResults.value = res.data['hydra:totalItems'];
        // uiStore.setLoading(false);
    } catch {
        Notification.addNotification('Error al cargar los vehiculos.', 'error', 6000);
        // uiStore.setLoading(false);
    } finally {
        uiStore.stopLoading();
    }
}

const getEmployees = async () => {
    let params = {
        itemsPerPage: 99999,
        active: true,
    }
    params['order[name]'] = 'ASC';
    try {
        const res = await EmployeeService.getEmployees(params);
        employees.value = res.data['hydra:member'];
        employeesOriginal.value = res.data['hydra:member']
    } catch {
        uiStore.stopLoading();
    }
}

const getVehicles = async () => {
    let params = {
        itemsPerPage: 99999,
    }
    try {
        const res = await VehicleService.getVehicles(params);
        vehicles.value = res.data['hydra:member'];
    } catch {
        uiStore.stopLoading();
    }
}

// Pagination
const onPageChange = (newPage) => {
    page.value = newPage;
    getVehicleRequests();
};

const onSizeChange = (newSize) => {
    itemsPerPage.value = newSize;
    if (page.value > Math.ceil(totalResults.value / newSize)) {
        page.value = 1;
    }
    getVehicleRequests();
};

// Search Dates
const calculateDisabledDates = (time) => {
    if (selectedFromDate.value) {
        let yesterday = new Date(selectedFromDate.value);
        yesterday.setDate(yesterday.getDate() - 1);
        return time.getTime() < yesterday.getTime();
    } else {
        return false;
    }
}

const dueDatePickerOptions = ref({
    firstDayOfWeek: 1,
    disabledDate: calculateDisabledDates
});

const disableFromDates = (date) => {
    if (selectedFromDate.value) {
        const fechaDesdeDate = new Date(selectedFromDate.value);
        const dateToCheck = new Date(date);

        // Permitir el mismo día, pero no fechas anteriores
        return checkDateSameDay(fechaDesdeDate, dateToCheck);
    }
    return false
}
const disableToDates = (date) => {
    if (selectedToDate.value) {
        const fechaHastaDate = new Date(selectedToDate.value);
        const dateToCheck = new Date(date);

        // Permitir el mismo día, pero no fechas posteriores
        return checkDateSameDay(dateToCheck, fechaHastaDate);
    }
    return false
}

const checkDateSameDay = (date1, date2) => {
    return (
        date1.getFullYear() > date2.getFullYear() ||
        (date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() > date2.getMonth()) ||
        (date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() > date2.getDate())
    );
}

const manageChangeFrom = (val) => {
    if (selectedToDate.value && val > selectedToDate.value) {
        const fechaDesdeDate = new Date(val);
        const fechaHastaDate = new Date(selectedToDate.value);

        // Si la fecha seleccionada es posterior a la fecha hasta, 
        // reset la fecha hasta
        if (fechaDesdeDate > fechaHastaDate) {
            selectedToDate.value = null;
        }
    }
    search();
}

const manageChangeTo = (val) => {
    if (selectedFromDate.value && val < selectedFromDate.value) {
        const fechaDesdeDate = new Date(val);
        const fechaHastaDate = new Date(selectedFromDate.value);

        // Si la fecha seleccionada es posterior a la fecha hasta, 
        // reset la fecha hasta
        if (fechaDesdeDate < fechaHastaDate) {
            selectedFromDate.value = null;
        }
    }
    search();
}

// Formats table
const getStatusText = (status) => {
    switch (status) {
        case 'pending':
            return 'Pendiente';
        case 'approved':
            return 'Aprobado';
        case 'rejected':
            return 'Rechazado';
        case 'cancelled':
            return 'Cancelado';
        case 'closed':
            return 'Cerrado';
        case 'confirmed':
            return 'Confirmado';
        case 'returned':
            return 'Devuelto';
        default:
            return '';
    }
}

const vehicleRequestStatusClass = (status) => {
    switch (status) {
        case 'pending':
            return 'status-pending';
        case 'approved':
            return 'status-approved';
        case 'rejected':
        case 'cancelled':
            return 'status-rejected';
        case 'closed':
        case 'returned':
        case 'confirmed':
            return 'status-closed';
        default:
            return '';
    }
}

const formatDate = (dateString) => {
    if (!dateString) return ''

    const date = new Date(dateString)

    return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

// Vehicle Request Dialog
const openDialogVehicleRequest = (selected = null) => {
    dialogRequest.value = true;
    selectedRequest.value = selected;
}
const closeDialogVehicleRequest = () => {
    dialogRequest.value = false;
    if (closingRequestForAction.value) {
        closingRequestForAction.value = false;
        return;
    }
    selectedRequest.value = null;
    search();
}

// Open and close Dialogs
const requestVehicle = () => {
    dialogNewRequest.value = true;
}

const closeDialogNewVehicleRequest = () => {
    dialogNewRequest.value = false;
}

const openDialogRejectRequest = (req) => {
    selectedRequest.value = req;
    dialogRejectRequest.value = true;
}

const closeDialogRejectVehicleRequest = () => {
    dialogRejectRequest.value = false;
    selectedRequest.value = null;
}

const openDialogCloseRequest = (req) => {
    selectedRequest.value = req;
    dialogReturnRequest.value = true;
}

const closeDialogReturnVehicleRequest = () => {
    dialogReturnRequest.value = false;
    selectedRequest.value = null;
    search();
}

const cancelDialogReturnVehicleRequest = () => {
    if (vehicleReturnRef.value) {
        vehicleReturnRef.value.removeAllTickets();
    }
    closeDialogReturnVehicleRequest();
}

const openDialogCancelRequest = (req) => {
    selectedRequest.value = req;
    dialogCancelRequest.value = true;
}
const closeDialogCancelVehicleRequest = () => {
    dialogCancelRequest.value = false;
    selectedRequest.value = null;
}

const openDialogConfirmRequest = (req) => {
    selectedRequest.value = req;
    dialogConfirmRequest.value = true;
}

const closeDialogConfirmVehicleRequest = () => {
    dialogConfirmRequest.value = false;
    selectedRequest.value = null;
}

const openRequestActionFromDetail = (req, action) => {
    selectedRequest.value = req;
    closingRequestForAction.value = true;
    dialogRequest.value = false;

    switch (action) {
        case 'return':
            dialogReturnRequest.value = true;
            break;
        case 'cancel':
            dialogCancelRequest.value = true;
            break;
        case 'reject':
            dialogRejectRequest.value = true;
            break;
        case 'confirm':
            dialogConfirmRequest.value = true;
            break;
        default:
            break;
    }
}

// Buttons Table
const canReturnVehicle = (v) => {
    if (!v) return false;
    if (user.value.id === v.employee?.id) {
        if (v.status && v.status === 'approved') {
            return true;
        }
    }
    return false;
}

const canModifyRequest = (v) => {
    if (!v) return false;
    if (canApproveVehicleRequests.value && v.status === 'pending') {
        const today = new Date();
        const temp = new Date(v.startDate)
        const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        if (endOfToday <= temp) {
            return true;
        }

    }
    return false;
}
const canRejectRequest = (v) => {
    if (!v) return false;
    if (canApproveVehicleRequests.value && v.status === 'pending') {
        const today = new Date();
        const temp = new Date(v.startDate)
        const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        if (endOfToday <= temp) {
            return true;
        }
    }
    if (user.value.id === v.employee?.id) {
        if (v.status && v.status === 'pending') return true;
    }
    return false;
}

const canCancelVehicle = (v) => {
    if (!v) return false;
    if (canApproveVehicleRequests.value && v.status && v.status === 'approved') return true;
    if (user.value.id === v.employee?.id) {
        if (user.value.roles.includes('ROLE_ADMIN') && v.status && v.status === 'pending') return false;
        if (v.status && v.status === 'approved') return true;
    }
    return false;
}

const canEditRequest = (v) => {
    if (!v) return false;
    if (user.value.id === v.employee?.id) {
        if (v.status && v.status === 'pending') {
            return true;
        }
    }
    if (user.value.roles.includes('ROLE_ADMIN') && v.status && (v.status === 'returned' || v.status === 'closed')) return true;
    return false;
}

const canConfirmRequest = (v) => {
    if (!v) return false;
    if (user.value.roles.includes('ROLE_ADMIN') && v.status && v.status === 'returned') return true;
    return false;
}

const approveRequest = async (req) => {
    if (!canApproveVehicleRequests.value) return;

    uiStore.startLoading();
    try {
        const res = await vehicleRequestService.approveVehicleRequest(req.id)
        if (res && (res.status === 200 || res.status === 201)) {
            notification.addNotification({
                type: 'success',
                message: 'Solicitud aprobada',
                duration: 4000
            })
            await search();
        }
    } finally {
        uiStore.stopLoading();
    }
}

// Filtro Empleados

// Método de filtrado personalizado
const filterUsers = (query) => {
    employees.value = filterByNormalizedText(employeesOriginal.value, query, employee => employee.fullName);
};

</script>

<template>
    <div>
        <HeaderTitle title="Vehículos" />
        <div class="d-flex row vehicle-request-header">
            <div class="d-flex row justify-content-start el-col-xs-24 col-md-6">
                <div class="col-md-12 d-flex">
                    <IconVehicle class="iconVehicle" />
                    <div class="title-text ml-4">
                        Solicitudes de Vehículos
                    </div>
                </div>
            </div>
            <div class="d-flex flex-row justify-content-end el-col-xs-24 col-md-6 vehicle-filter-options">
                <div class="filter-options">
                    <template v-for="(item, index) in filter" :key="index">
                        <span class="option" :class="{ active: item.active }" @click="changeFilter(index)">
                            {{ item.name }}
                        </span>
                        <span v-if="index < filter.length - 1" class="separator">|</span>
                    </template>
                </div>
            </div>

        </div>
        <el-row class="mt-2">
            <el-card class="card-search card-abs w-100 wback" body-style="padding:0">
                <div class="search-form">
                    <div class="row vehicle-request-search-row">
                        <div class="col-4 el-col-xs-24">
                            <div class="form-item">
                                <label for="vehicle">Vehículo</label>
                                <el-select v-model="vehicleId" placeholder="Todos" filterable clearable id="vehicle"
                                    @change="search()">
                                    <el-option v-for="u in vehicles" :key="u.id"
                                        :label="u.displayName + ' --- ' + u.licensePlate" :value="u.id">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="col-4 el-col-xs-24">
                            <div class="form-item">
                                <label class="w-100" for="user">Empleado</label>
                                <el-select v-model="vehicleUser" placeholder="Todos" filterable clearable id="user"
                                    @change="search()" :filter-method="filterUsers">
                                    <el-option v-for="e in employees" :key="e.id" :label="e.fullName" :value="e.id">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="col-4 el-col-xs-24">
                            <div class="form-item">
                                <label class="w-100" for="user">Estado</label>
                                <el-select v-model="requestStatus" placeholder="Todos" filterable clearable id="user"
                                    @change="status = requestStatus; search()">
                                    <el-option v-for="u in allStatus" :key="u.value" :label="u.name" :value="u.value">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                    </div>
                    <div class="row vehicle-request-search-actions-row" style="margin-top: 20px">
                        <div class="col-md-8 el-col-xs-24">
                            <div class="row">
                                <div class="col-md-6 el-col-xs-24">
                                    <div class="form-item date-picker label-common">
                                        <label class="mb-2 w-100" for="dateFrom">Desde</label>
                                        <el-date-picker class="w-100" v-model="selectedFromDate" type="date" clearable
                                            id="dateFrom" format="DD/MM/YYYY" value-format="YYYY-MM-DD"
                                            :prefix-icon="customPrefix" :picker-options="{ firstDayOfWeek: 1 }"
                                            :disabled-date="disableToDates" @change="manageChangeFrom" />
                                    </div>
                                </div>
                                <div class="col-md-6 el-col-xs-24">
                                    <div class="form-item date-picker label-common">
                                        <label class="w-100 mb-2" for="dateTo">Hasta</label>
                                        <el-date-picker v-model="selectedToDate" class="w-100" type="date" clearable
                                            id="dateTo" format="DD/MM/YYYY" value-format="YYYY-MM-DD"
                                            :prefix-icon="customPrefix" :picker-options="dueDatePickerOptions"
                                            :disabled-date="disableFromDates" @change="manageChangeTo" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4 row d-flex el-col-xs-24">
                            <div
                                class="d-flex justify-content-center align-items-center col-md-12 vehicle-request-buttons">
                                <div
                                    class="col-md-6 align-self-center justify-content-center vehicle-request-button-col">
                                    <button type="submit" @click="requestVehicle()" class="btn btn_red btn__new">
                                        Solicitar
                                    </button>
                                </div>
                                <div
                                    class="col-md-6 align-self-center justify-content-center vehicle-request-button-col">
                                    <button type="submit" @click="search()" class="btn btn_yellow btn__search">
                                        BUSCAR
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </el-card>
        </el-row>
        <div class="table table-users table-not-mobile">
            <div v-if="vehicleRequests && vehicleRequests.length > 0">
                <table>
                    <thead class="table-header">
                        <tr class="justify-content-center gap-2">
                            <th>
                                COCHE
                            </th>
                            <th>
                                USUARIO
                            </th>
                            <th>
                                FECHAS
                            </th>
                            <th v-if="hasKms">
                                KMS
                            </th>
                            <th>
                                ESTADO
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="v in vehicleRequests" :key="v.id" @click="openDialogVehicleRequest(v)">
                            <td class="pointer">
                                {{ v.vehicle?.displayName + ' --- ' + v.vehicle?.licensePlate }}
                            </td>
                            <td class="pointer">
                                {{ v.employee?.fullName }}
                            </td>
                            <td class="pointer">
                                {{ formatDate(v.startDate) + ' - ' + formatDate(v.endDate) }}
                            </td>
                            <td class="pointer" v-if="hasKms">
                                {{ v.traveledKm }}
                            </td>
                            <td class="pointer">
                                <span :class="vehicleRequestStatusClass(v.status)">
                                    {{ getStatusText(v.status) }}
                                </span>
                            </td>
                            <td style="text-align: center;" v-if="hasButtons">
                                <div class="buttons d-inline-flex">
                                    <!-- Return -->
                                    <el-button class="btn btn_red" v-if="canReturnVehicle(v)"
                                        @click.stop="openDialogCloseRequest(v)"
                                        style="text-transform: uppercase; font-weight: bolder; height: 35px;">
                                        <IconRestart class="iconRestart" />
                                        DEVOLVER
                                    </el-button>
                                    <!-- Cancel -->
                                    <div class="pointer" @click.stop="openDialogCancelRequest(v)"
                                        v-if="canCancelVehicle(v)" style="margin-top: 3px;">
                                        <font-awesome-layers class="fa-2x" title="Cancelar solicitud"
                                            @click="openDialogCancelRequest(v)">
                                            <font-awesome-icon icon="circle" style="color:red" />
                                            <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                                        </font-awesome-layers>
                                    </div>
                                    <!-- Reject -->
                                    <el-button class="btn btn_red align-self-center justify-content-end"
                                        :icon="CloseBold" circle v-if="canRejectRequest(v)"
                                        @click.stop="openDialogRejectRequest(v)" style="margin-left: -10px;"
                                        title="Rechazar solicitud" />
                                    <!-- Accept -->
                                    <el-button class="btn align-self-center justify-content-end" :icon="Select" circle
                                        title="Aprobar solicitud" v-if="canModifyRequest(v)"
                                        @click.stop="approveRequest(v)"
                                        style="margin-left: -7px; color: #fff; background-color: #474ce6;" />
                                    <!-- Confirm -->
                                    <el-button class="btn align-self-center justify-content-end" :icon="Select" circle
                                        title="Confirmar recepción" v-if="canConfirmRequest(v)"
                                        @click.stop="openDialogConfirmRequest(v)"
                                        style="color: #fff; background-color: #28a745; margin-right: 0px;" />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else>
                <h5>No se han encontrado Vehículos según los filtros especificados...</h5>
            </div>
        </div>
        <div class="table-mobile mb-4 mt-3">
            <div v-if="vehicleRequests && vehicleRequests.length > 0">
                <div v-for="v in vehicleRequests" :key="v.id">
                    <el-row class="mt-3">
                        <el-card class="card-vehicle-request-item">
                            <div class="vehicle-request-card pointer" @click="openDialogVehicleRequest(v)">
                                <div class="buttons d-inline-flex vehicle-request-card-actions-top" v-if="hasButtons">
                                    <div class="pointer vehicle-icon-action" @click.stop="openDialogCancelRequest(v)"
                                        v-if="canCancelVehicle(v)">
                                        <font-awesome-layers class="fa-2x" title="Cancelar solicitud">
                                            <font-awesome-icon icon="circle" style="color:red" />
                                            <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                                        </font-awesome-layers>
                                    </div>
                                    <el-button class="btn btn_red vehicle-round-action" :icon="CloseBold" circle
                                        v-if="canRejectRequest(v)" @click.stop="openDialogRejectRequest(v)"
                                        title="Rechazar solicitud" />
                                    <el-button class="btn vehicle-round-action" :icon="Select" circle
                                        title="Aprobar solicitud" v-if="canModifyRequest(v)"
                                        @click.stop="approveRequest(v)"
                                        style="color: #fff; background-color: #474ce6;" />
                                    <el-button class="btn vehicle-round-action" :icon="Select" circle
                                        title="Confirmar recepción" v-if="canConfirmRequest(v)"
                                        @click.stop="openDialogConfirmRequest(v)"
                                        style="color: #fff; background-color: #28a745;" />
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-request-label">Coche: </span>
                                    {{ v.vehicle?.displayName + ' --- ' + v.vehicle?.licensePlate }}
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-request-label">Usuario: </span>{{ v.employee?.fullName }}
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-request-label">Inicio: </span>{{ formatDate(v.startDate) }}
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-request-label">Fin: </span>{{ formatDate(v.endDate) }}
                                </div>
                                <div class="el-col-xs-24" v-if="hasKms">
                                    <span class="vehicle-request-label">Kms: </span>{{ v.traveledKm }}
                                </div>
                                <div class="el-col-xs-24">
                                    <span class="vehicle-request-label">Estado: </span>
                                    <span :class="vehicleRequestStatusClass(v.status)">
                                        {{ getStatusText(v.status) }}
                                    </span>
                                </div>
                                <div class="buttons d-inline-flex vehicle-request-card-actions-bottom"
                                    v-if="hasButtons && canReturnVehicle(v)">
                                    <el-button class="btn btn_red vehicle-action-button" v-if="canReturnVehicle(v)"
                                        @click.stop="openDialogCloseRequest(v)">
                                        <IconRestart class="iconRestart" />
                                        DEVOLVER
                                    </el-button>
                                </div>
                            </div>
                        </el-card>
                    </el-row>
                </div>
            </div>
            <div v-else>
                <h5>No se han encontrado Vehículos según los filtros especificados...</h5>
            </div>
        </div>
        <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" size="large"
            :page-size-options="[10, 20, 50, 100, 200]" layout="prev, pager, next, sizes" :total="totalResults"
            class="tracker-pagination" show-always @current-change="onPageChange" @size-change="onSizeChange" />
        <!-- Modal Show Request -->
        <el-dialog :model-value="dialogRequest" :center="true" :lock-scroll="true" :show-close="false" width="750"
            class="vehicle-dialog" @close="closeDialogVehicleRequest()" style="border-radius: 20px;">
            <VehicleRequestDialog @close="closeDialogVehicleRequest()" :vehicleRequest="selectedRequest"
                :canEdit="canEditRequest(selectedRequest)" :canReturn="canReturnVehicle(selectedRequest)"
                :canCancel="canCancelVehicle(selectedRequest)" :canApprove="canModifyRequest(selectedRequest)"
                :canReject="canRejectRequest(selectedRequest)" :canConfirm="canConfirmRequest(selectedRequest)"
                @return-request="openRequestActionFromDetail($event, 'return')" @finish="search()"
                v-if="dialogRequest" />
        </el-dialog>
        <!-- Modal New Request -->
        <el-dialog :model-value="dialogNewRequest" :center="true" :lock-scroll="true" :show-close="false" :width="750"
            class="vehicle-dialog" @close="closeDialogNewVehicleRequest()" style="border-radius: 20px;">
            <VehicleNewRequest @close="closeDialogNewVehicleRequest()" @finish="search()" v-if="dialogNewRequest" />
        </el-dialog>
        <!-- Modal Cancel Request -->
        <el-dialog :model-value="dialogCancelRequest" :center="true" :lock-scroll="true" :show-close="false"
            class="vehicle-dialog" :width="750" @close="closeDialogCancelVehicleRequest()" style="border-radius: 20px;">
            <VehicleCancelDialog @close="closeDialogCancelVehicleRequest()" @finish="search()"
                :vehicleRequest="selectedRequest" v-if="dialogCancelRequest" />
        </el-dialog>
        <!-- Modal Reject Request -->
        <el-dialog :model-value="dialogRejectRequest" :center="true" :lock-scroll="true" :show-close="false"
            class="vehicle-dialog" :width="750" @close="closeDialogRejectVehicleRequest()" style="border-radius: 20px;">
            <VehicleRejectDialog @close="closeDialogRejectVehicleRequest()" @finish="search()"
                :vehicleRequest="selectedRequest" v-if="dialogRejectRequest" />
        </el-dialog>
        <!-- Modal Return-->
        <el-dialog :model-value="dialogReturnRequest" :center="true" :lock-scroll="true" :show-close="false"
            class="vehicle-dialog" :width="750" @close="cancelDialogReturnVehicleRequest()"
            style="border-radius: 20px;">
            <VehicleReturn @close="closeDialogReturnVehicleRequest()" @finish="search()" ref="vehicleReturnRef"
                :vehicleRequest="selectedRequest" v-if="dialogReturnRequest" />
        </el-dialog>
        <!-- Modal Confirm Request -->
        <el-dialog :model-value="dialogConfirmRequest" :center="true" :lock-scroll="true" :show-close="false"
            class="vehicle-dialog" :width="750" @close="closeDialogConfirmVehicleRequest()"
            style="border-radius: 20px;">
            <VehicleConfirmDialog @close="closeDialogConfirmVehicleRequest()" @finish="search()"
                :vehicleRequest="selectedRequest" v-if="dialogConfirmRequest" />
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
.card-search {
    border-radius: 15px;
    height: 150px;
    background: url("../../assets/icons/car.jpg"), white;
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: 69%;
    overflow: visible !important;
    background-size: 26%;
}

.btn__search {
    color: $bg-blue;
    text-transform: uppercase;
    font-weight: bolder;
    margin-top: 25px;
    height: 40px;
}

.btn__new {
    color: white;
    text-transform: uppercase;
    font-weight: bolder;
    margin-top: 25px;
    height: 40px;
}

.iconVehicle {
    width: 30px;
    fill: $bg-blue;
}

.iconRestart {
    width: 20px;
    fill: white;
    margin-right: 6px;
}

.iconThumbs {
    width: 20px;
    fill: white;
}

.table-header {

    th,
    tr {
        vertical-align: middle;
    }
}

.btn_modify {
    text-transform: uppercase;
    font-weight: bolder;
    height: 35px;
    width: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
}

.filter-options {
    font-weight: bold;
    font-size: 32px;
}

.option {
    cursor: pointer;
    color: #3B47FF;
    /* Azul por defecto */
    margin: 0 0.2em;
    font-size: 20px;
}

.option.active {
    color: #FF006E;
    /* Rosa cuando está activo */
}

.separator {
    color: #3B47FF;
    /* Separador siempre azul */
    margin: 0 0.2em;
    font-size: 20px;
}

.table-mobile {
    display: none;
}

.card-vehicle-request-item {
    border-radius: 18px;
}

.vehicle-request-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 8px;
    padding-bottom: 10px;
}

.vehicle-request-label {
    color: $bg-blue;
    font-weight: 700;
}

.vehicle-request-card-actions-top {
    position: absolute;
    top: 0;
    right: 0;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
}

.vehicle-request-card-actions-bottom {
    width: 100%;
    margin-top: 6px;
    justify-content: center;
}

.vehicle-action-button {
    text-transform: uppercase;
    font-weight: bolder;
    min-height: 35px;
}

.vehicle-round-action {
    margin-top: 6px;
    margin-left: 0 !important;
    margin-right: 0 !important;
}

.status-pending,
.status-approved,
.status-rejected,
.status-closed {
    display: inline-block;
    padding: 0.35rem 0.75rem;
    border-radius: 0.375rem;
    font-weight: 600;
    font-size: 0.95rem;
    color: #fff;
    text-align: center;
    text-transform: uppercase;
}

.status-pending {
    background-color: #f1c40f;
}

.status-approved {
    background-color: #27ae60;
}

.status-rejected {
    background-color: #E53935;
}

.status-closed {
    background-color: #107DDE;
}

.vehicle-icon-action {
    margin-top: 7px;
}

@media (max-width: 767px) {
    .vehicle-request-header {
        row-gap: 2px;
    }

    .vehicle-request-header .iconVehicle {
        width: 24px;
        min-width: 24px;
        margin-top: 6px;
    }

    .vehicle-request-header .title-text {
        margin-left: 12px !important;
        font-size: 2.25rem;
        line-height: 1.05;
        text-align: left;
    }

    .vehicle-filter-options {
        justify-content: flex-start !important;
        margin-top: 4px;
    }

    .filter-options {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        line-height: 1.2;
        font-size: 18px;
        white-space: nowrap;
    }

    .card-search {
        height: auto;
        background-size: 34%;
        background-position-y: bottom;
    }

    .vehicle-request-search-row,
    .vehicle-request-search-actions-row {
        row-gap: 5px;
        margin-top: 0px !important;
    }

    .vehicle-request-buttons {
        flex-direction: column;
        align-items: stretch !important;
        gap: 12px;
        justify-content: center !important;
    }

    .vehicle-request-button-col {
        width: 100%;
        padding: 0;
        display: flex;
        justify-content: center;
    }

    .btn__new,
    .btn__search {
        width: 70%;
        min-width: 210px;
        margin-top: 0;
        margin-left: auto;
        margin-right: auto;
    }

    .table-not-mobile {
        display: none;
    }

    .table-mobile {
        display: block;
    }

    :deep(.vehicle-dialog .el-dialog) {
        width: calc(100vw - 24px) !important;
        max-width: 750px;
        margin: 12px auto;
    }
}

@media (min-width: 768px) {
    .table-mobile {
        display: none;
    }
}
</style>
